import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { CONTACT } from 'config/contact';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { ContactFormData, FormErrors } from '../types';
import { contactService } from '../../../services/contactService';


interface ContactFormProps {
  onSubmit: (data: ContactFormData) => void;
  isSubmitting: boolean;
}

const ContactForm = ({ onSubmit, isSubmitting }: ContactFormProps) => {
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    companySize: '',
    industry: '',
    automationGoals: [],
    currentChallenges: '',
    monthlyTimeSpent: '',
    preferredContactMethod: '',
    urgency: '',
    hearAboutUs: '',
    additionalNotes: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const companySizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-100', label: '51-100 employees' },
    { value: '101-500', label: '101-500 employees' },
    { value: '500+', label: '500+ employees' }
  ];

  const industryOptions = [
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'realestate', label: 'Real Estate' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'agency', label: 'Digital Agency' },
    { value: 'saas', label: 'SaaS' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'other', label: 'Other' }
  ];

  const timeSpentOptions = [
    { value: '10-20', label: '10-20 hours/month' },
    { value: '20-40', label: '20-40 hours/month' },
    { value: '40-70', label: '40-70 hours/month' },
    { value: '70+', label: '70+ hours/month' }
  ];

  const contactMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'video', label: 'Video Call' }
  ];

  const urgencyOptions = [
    { value: 'immediate', label: 'Immediate (This Week)' },
    { value: 'soon', label: 'Soon (Next 2 Weeks)' },
    { value: 'exploring', label: 'Exploring Options' },
    { value: 'planning', label: 'Planning for Future' }
  ];

  const automationGoalOptions = [
    'Save Time',
    'Reduce Costs',
    'Improve Accuracy',
    'Scale Operations',
    'Better Customer Experience',
    'Data Integration'
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData.companySize) {
      newErrors.companySize = 'Please select company size';
    }

    if (!formData.industry) {
      newErrors.industry = 'Please select your industry';
    }

    if (formData.automationGoals.length === 0) {
      newErrors.automationGoals = 'Please select at least one automation goal';
    }

    if (!agreedToTerms) {
      newErrors.terms = 'You must agree to the terms and privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitStatus('loading');
    setSubmitMessage('');

    try {
      const result = await contactService.submitContactForm({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        companySize: formData.companySize,
        industry: formData.industry,
        automationGoals: formData.automationGoals,
        currentChallenges: formData.currentChallenges,
        monthlyTimeSpent: formData.monthlyTimeSpent,
        preferredContactMethod: formData.preferredContactMethod,
        urgency: formData.urgency,
        hearAboutUs: formData.hearAboutUs,
        additionalNotes: formData.additionalNotes
      });

      const dbSaved = (result as any)?.dbSaved ?? true;
      const emailSent = (result as any)?.emailSent ?? true;

      setSubmitStatus('success');
      if (!dbSaved && emailSent) {
        setSubmitMessage('Thanks — we sent a confirmation email, but we could not save your submission to the database. Our team has been notified and will follow up shortly.');
      } else if (dbSaved && !emailSent) {
        setSubmitMessage("Thank you for your submission! We saved your request, but we couldn't send a confirmation email. We will follow up manually.");
      } else if (!dbSaved && !emailSent) {
        setSubmitStatus('error');
        setSubmitMessage("We couldn't complete your request at the moment — please try again or contact us by WhatsApp.");
      } else {
        setSubmitMessage('Thank you for your submission! We will get back to you within 2 hours.');
      }

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        companySize: '',
        industry: '',
        automationGoals: [],
        currentChallenges: '',
        monthlyTimeSpent: '',
        preferredContactMethod: '',
        urgency: '',
        hearAboutUs: '',
        additionalNotes: ''
      });
      setAgreedToTerms(false);

      // Call parent onSubmit if provided
      if (onSubmit) onSubmit(formData);

      // Scroll to top after successful submission
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setSubmitStatus('error');
      setSubmitMessage(err?.message || 'There was an error submitting your form. Please try again or contact us via WhatsApp.');
      console.error('Form submission error:', err);
    }
  };

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleGoalToggle = (goal: string) => {
    setFormData(prev => ({
      ...prev,
      automationGoals: prev.automationGoals.includes(goal)
        ? prev.automationGoals.filter(g => g !== goal)
        : [...prev.automationGoals, goal]
    }));
    if (errors.automationGoals) {
      setErrors(prev => ({ ...prev, automationGoals: '' }));
    }
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-brand-primary mb-3">
              Book Your Free Automation Audit
            </h2>
            <p className="text-text-secondary">
              Fill out the form below and we will get back to you within 2 hours
            </p>
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <p className="text-success">{submitMessage}</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-error">{submitMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Full Name"
                  name="fullName"
                  autoComplete="name"
                  type="text"
                
                  placeholder="Your full name"
                  value={formData.fullName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('fullName', e.target.value)}
                  error={errors.fullName}
                  required
                />

                <Input
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  type="email"
                
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                  error={errors.email}
                  required
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Phone Number"
                  name="phone"
                  autoComplete="tel"
                  type="tel"
                
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('phone', e.target.value)}
                  error={errors.phone}
                  required
                />

              <Input
                label="Company Name"
                
                autoComplete="organization"
                type="text"
                name="company"
                placeholder="Your company name"
                value={formData.company}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('company', e.target.value)}
                error={errors.company}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                
                label="Company Size"
                name="companySize"
                options={companySizeOptions}
                value={formData.companySize}
                onChange={(value) => handleInputChange('companySize', value as string)}
                error={errors.companySize}
                placeholder="Select company size"
                required
              />

              <Select
                
                label="Industry"
                name="industry"
                options={industryOptions}
                value={formData.industry}
                onChange={(value) => handleInputChange('industry', value as string)}
                error={errors.industry}
                placeholder="Select your industry"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Automation Goals <span className="text-error">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {automationGoalOptions.map((goal) => (
                  <button
                    key={goal}
                    type="button"
                    onClick={() => handleGoalToggle(goal)}
                    className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-fast ${
                      formData.automationGoals.includes(goal)
                        ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-text-secondary hover:border-primary/50'
                    }`}
                  >
                    {goal}
                  </button>
                ))}
              </div>
              {errors.automationGoals && (
                <p className="mt-2 text-sm text-error">{errors.automationGoals}</p>
              )}
            </div>

            <Input
              label="Current Challenges"
              
              autoComplete="off"
              type="text"
              name="currentChallenges"
              placeholder="Describe your biggest automation challenges..."
              value={formData.currentChallenges}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('currentChallenges', e.target.value)}
              description="Help us understand your specific pain points"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                
                label="Time Spent on Manual Tasks"
                name="monthlyTimeSpent"
                options={timeSpentOptions}
                value={formData.monthlyTimeSpent}
                onChange={(value) => handleInputChange('monthlyTimeSpent', value as string)}
                placeholder="Select time range"
              />

              <Select
                
                label="Preferred Contact Method"
                name="preferredContactMethod"
                options={contactMethodOptions}
                value={formData.preferredContactMethod}
                onChange={(value) => handleInputChange('preferredContactMethod', value as string)}
                placeholder="How should we reach you?"
              />
            </div>

            <Select
              
              label="Urgency Level"
              name="urgency"
              options={urgencyOptions}
              value={formData.urgency}
              onChange={(value) => handleInputChange('urgency', value as string)}
              placeholder="When do you need automation?"
            />

            <Input
              label="Additional Notes"
              
              autoComplete="off"
              type="text"
              name="additionalNotes"
              placeholder="Any specific requirements or questions?"
              value={formData.additionalNotes}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('additionalNotes', e.target.value)}
            />

            <div className="border-t border-border pt-6">
              <Checkbox
                label="I agree to the Terms of Service and Privacy Policy"
                name="agreedToTerms"
                // browsers don't need autocomplete for agreement checkbox
                checked={agreedToTerms}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAgreedToTerms(e.target.checked);
                  if (errors.terms) {
                    setErrors(prev => ({ ...prev, terms: '' }));
                  }
                }}
                error={errors.terms}
                required
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={submitStatus === 'loading'}
                iconName="Send"
                iconPosition="right"
                disabled={submitStatus === 'loading'}
              >
                {submitStatus === 'loading' ? 'Submitting...' : 'Submit Audit Request'}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                onClick={() => window.open(CONTACT.whatsappLink, '_blank')}
                disabled={submitStatus === 'loading'}
              >
                Chat on WhatsApp
              </Button>
            </div>

            <div className="flex items-center justify-center space-x-6 text-sm text-text-secondary pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span>SSL Secured</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Lock" size={16} className="text-success" />
                <span>Data Protected</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
